<script setup lang="ts">
import { computed, type PropType } from 'vue'
import { useHelpers, useTranslate } from '@/shared/lib'

const props = defineProps({
  data: {
    type: Array as PropType<Array<[number, number, number]>>,
    required: true,
  },
})

const { trs } = useTranslate()
const { generateKey } = useHelpers()

/* Максимальное значение 23*60 + 59 */
const total = 1439
const key = generateKey()

/* Вычисляем значения графика */
const sections = computed(() => {
  const list: any[] = []

  const sortedData = [...props.data].sort((a, b) => {
    const timeA = a[0] * 60 + Number(a[1])
    const timeB = b[0] * 60 + Number(b[1])
    return timeA - timeB
  })

  /* Добавляем начальную секцию (00:00 - первый интервал) */
  if (sortedData.length > 0 && (sortedData[0][0] !== 0 || sortedData[0][1] !== 0)) {
    list.push({
      start: { hours: 0, minutes: 0 },
      end: { hours: sortedData[0][0], minutes: sortedData[0][1] },
      state: 1,
    })
  }

  /* Добавляем промежуточные секции */
  for (let i = 0; i < sortedData.length; i++) {
    const current = sortedData[i]
    const next = sortedData[i + 1]

    if (next) {
      list.push({
        start: { hours: current[0], minutes: current[1] },
        end: { hours: next[0], minutes: next[1] },
        state: current[2],
      })
    } else {
      list.push({
        start: { hours: current[0], minutes: current[1] },
        end: { hours: 23, minutes: 59 },
        state: current[2],
      })
    }
  }

  /* Если нет данных, добавляем всю секцию как включенную */
  if (sortedData.length === 0) {
    list.push({
      start: { hours: 0, minutes: 0 },
      end: { hours: 23, minutes: 59 },
      state: 0,
    })
  }

  list[0].state = list[list.length - 1].state

  /* Дополнительные вычисления */
  return list.map((section: any) => {
    const startMinutes = section.start.hours * 60 + Number(section.start.minutes)
    const endMinutes = section.end.hours * 60 + Number(section.end.minutes)
    const duration = endMinutes - startMinutes
    const widthPercent = (duration / total) * 100

    const label = section.state ? trs('itf.on_short') : trs('itf.off_short')
    const startString = `${section.start.hours.toString().padStart(2, 0)}:${section.start.minutes.toString().padStart(2, 0)}`
    const endString = `${section.end.hours.toString().padStart(2, 0)}:${section.end.minutes.toString().padStart(2, 0)}`

    const tooltipLabel = `${startString} - ${endString}
     ${label}`

    return {
      ...section,
      width: widthPercent,
      stateClass: section.state
        ? 'schedule-program__chart-section_on'
        : 'schedule-program__chart-section_off',
      isShowLabel: widthPercent >= 7,
      endString,
      label,
      tooltipLabel,
      key: generateKey(),
    }
  })
})
</script>

<template>
  <div class="schedule-program__chart">
    <template v-for="(item, index) in sections" :key="index">
      <div
        class="schedule-program__chart-section"
        :id="`time_slice_${key}_${index}`"
        :style="{ width: `${item.width}%` }"
        :class="item.stateClass"
      >
        <span v-if="item.isShowLabel">
          {{ item.label }}
        </span>

        <span v-if="item.isShowLabel" class="schedule-program__chart-time">
          {{ item.endString }}
        </span>
      </div>

      <VTooltip
        :activator="`#time_slice_${key}_${index}`"
        location="top"
        :open-delay="1000"
        :text="item.tooltipLabel"
      />
    </template>
  </div>
</template>
